import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import { useTrace } from '../../utils/trace';
/**
 * A simple component to display a message when there is no data to show.
 *
 * It takes a title, message, and optional image and children, displaying them in a centered grid.
 *
 * [Documentation](https://wds2docs.dev-sqnt.com/?path=/docs/components-emptystate)
 *
 * ### Example
 * ```tsx
 * <EmptyState
 *     title="Failed to load data"
 *     message="Please try again later"
 *     sx={{ background: 'purple' }}
 *     titleSx={{ color: 'red' }}
 *     imageSx={{ background: 'yellow' }}
 * />
 * ```
 */
export default function EmptyState({ title, message, image, variant = 'large', children, 'automation-id': automationId, sx, messageSx, imageSx, bodySx, titleSx, }) {
    const getFullTrace = useTrace(automationId !== null && automationId !== void 0 ? automationId : 'empty-state');
    const variantSizes = useCallback((sizes) => sizes[variant], [variant]);
    return (_jsxs(Grid, { container: true, justifyContent: "center", alignItems: "center", sx: Object.assign({ textAlign: 'center' }, sx), "automation-id": getFullTrace(), children: [image && (_jsx(Grid, { item: true, xs: 12, sx: Object.assign(Object.assign({ maxWidth: '100%', paddingLeft: '40px' }, (variant === 'xsmall' && {
                    paddingLeft: '20px',
                    paddingRight: '20px',
                })), imageSx), "automation-id": getFullTrace('image'), children: image })), _jsxs(Grid, { item: true, xs: 12, sm: variantSizes({ xsmall: 10, small: 10, large: 6 }), md: variantSizes({ xsmall: 10, small: 10, large: 4 }), sx: bodySx, "automation-id": getFullTrace('body'), children: [_jsx(Typography, { sx: Object.assign(Object.assign({ fontSize: variantSizes({ xsmall: 14, small: 15, large: 20 }) }, (variant === 'xsmall' && {
                            fontWeight: 'normal',
                            paddingTop: 1,
                        })), titleSx), variant: "h5", "automation-id": getFullTrace('title'), children: title }), _jsx(Typography, { sx: Object.assign(Object.assign({ padding: (theme) => theme.spacing(3, 0), fontSize: variantSizes({ xsmall: 12, small: 15, large: 15 }) }, (variant === 'xsmall' && {
                            color: (theme) => theme.palette.text.secondary,
                            paddingTop: 0,
                            paddingBottom: 2,
                        })), messageSx), variant: "body1", "automation-id": getFullTrace('message'), children: message }), children && _jsx(Grid, { "automation-id": getFullTrace('children'), children: children })] })] }));
}
//# sourceMappingURL=EmptyState.js.map